import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useConversations } from '../hooks/use-conversations';
import { useAgentPanels, AgentPanelsContext } from '../hooks/use-agent-panels';
import { useSlashCommands } from '../hooks/use-slash-commands';
import { useTasksContext } from '../providers/tasks-provider';
import { formatRelativeTime } from '../lib/format-relative-time';
import { AgentPanel } from './AgentPanel';
import { Button } from './Button';
import { ChatPanel } from './ChatPanel';
import { ConversationDrawer } from './ConversationDrawer';
import { ConversationListItem } from './ConversationListItem';
import { PanelLayout } from './PanelLayout';
import { Spinner } from './Spinner';
import { WelcomeScreen } from './WelcomeScreen';

import type { AgentStatus } from './ConversationListItem';
import type { TaskStage } from '../lib/types';

/** Derive a presentational AgentStatus from a linked task's stage + overlay + processing status. */
export function deriveAgentStatus(
  task: { stage: TaskStage; overlay: 'paused' | 'on-hold' | 'cancelled' | null } | undefined,
  processingStatus?: 'idle' | 'processing',
): AgentStatus {
  if (!task) {
    // No linked task — derive from conversation processing_status
    return processingStatus === 'processing' ? 'working' : 'idle';
  }
  if (task.overlay === 'paused') return 'paused';
  if (task.overlay === 'on-hold') return 'on-hold';
  if (task.overlay === 'cancelled') return 'cancelled';
  return task.stage;
}


export interface ConversationsViewProps {
  onConversationChange?: ((id: string | null) => void) | undefined;
}

export function ConversationsView({ onConversationChange, onTaskEvent, onTaskNavigate }: ConversationsViewProps = {}) {
  const { conversations, status, error, createConversation, selectedId, selectConversation, loadMore, hasMore, refreshConversation, updateConversationTitle, markRead, reload } = useConversations();
  const agentPanels = useAgentPanels();
  const { tasks } = useTasksContext();
  const taskByConversationId = useMemo(() => {
    const map = new Map<string, (typeof tasks)[number]>();
    for (const t of tasks) {
      if (t.conversation_id) map.set(t.conversation_id, t);
    }
    return map;
  }, [tasks]);
  const { commands: slashCommands, status: slashCommandStatus, fetch: fetchSlashCommands } = useSlashCommands();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState(280);
  const listRef = useRef<HTMLUListElement>(null);

  // Pending message from WelcomeScreen → ChatPanel bridge
  const pendingMessageRef = useRef<{ message: string; files?: File[] } | null>(null);

  // Auto-select the first conversation on initial load
  const autoSelected = useRef(false);
  useEffect(() => {
    if (!autoSelected.current && status === 'success' && conversations.length > 0 && selectedId === null) {
      autoSelected.current = true;
      selectConversation(conversations[0].id);
      onConversationChange?.(conversations[0].id);
    }
  }, [status, conversations, selectedId, selectConversation, onConversationChange]);

  const handleListScroll = useCallback((e: React.UIEvent<HTMLUListElement>) => {
    if (!hasMore) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      void loadMore();
    }
  }, [hasMore, loadMore]);

  const handleSelect = useCallback((id: string | null) => {
    selectConversation(id);
    if (id) {
      void markRead(id);
      void agentPanels.fetchAgentPanels(id);
    }
    onConversationChange?.(id);
  }, [selectConversation, markRead, agentPanels, onConversationChange]);

  const handleFirstMessage = useCallback(async (message: string, files?: File[]) => {
    pendingMessageRef.current = { message, files };
    await createConversation(message.slice(0, 100));
  }, [createConversation]);

  const handleMessageSent = useCallback((content: string) => {
    if (selectedId) {
      updateConversationTitle(selectedId, content.slice(0, 100));
    }
  }, [selectedId, updateConversationTitle]);

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center h-full p-sd-4 text-center">
        <p className="text-sd-error mb-sd-3">{error ?? 'Failed to load conversations'}</p>
        <Button variant="primary" onClick={reload}>Retry</Button>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <WelcomeScreen
        onFirstMessage={(msg, files) => { void handleFirstMessage(msg, files); }}
        slashCommands={slashCommands}
        slashCommandStatus={slashCommandStatus}
        onSlashCommandsFetch={fetchSlashCommands}
        onSlashCommandsRefresh={fetchSlashCommands}
      />
    );
  }

  const selectedConversation = selectedId ? conversations.find((c) => c.id === selectedId) : undefined;
  const chatPanel = (
    <ChatPanel
      hasPanelContent={agentPanels.tabs.length > 0}
      isPanelOpen={agentPanels.isPanelOpen}
      onTogglePanel={agentPanels.togglePanel}
      conversationId={selectedId}
      conversationTitle={selectedConversation?.title}
      onStreamComplete={refreshConversation}
      onMessageSent={handleMessageSent}
      pendingMessage={pendingMessageRef.current}
      onPendingMessageConsumed={() => { pendingMessageRef.current = null; }}
    />
  );

  const agentPanelEl = agentPanels.isPanelOpen ? (
    <AgentPanel hook={agentPanels} onResize={setPanelWidth} currentWidth={panelWidth} />
  ) : undefined;

  return (
    <AgentPanelsContext.Provider value={agentPanels}>
    <PanelLayout panel2={chatPanel} showDetail={selectedId !== null} panel3={agentPanelEl} panel3Width={agentPanelEl ? panelWidth : undefined}>
      {/* Mobile: hamburger button that opens ConversationDrawer overlay */}
      <div className="@md:hidden">
        <ConversationDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          conversations={conversations.map((c) => {
            const linkedTask = taskByConversationId.get(c.id);
            return {
              id: c.id,
              title: c.title,
              unread: c.unread,
              agentStatus: deriveAgentStatus(linkedTask, c.processing_status),
              ...(c.preview !== undefined ? { preview: c.preview } : {}),
              ...(c.updated_at !== undefined ? { timestamp: formatRelativeTime(c.updated_at) } : {}),
              ...(linkedTask ? { taskLinked: true, taskId: linkedTask.id, taskTitle: linkedTask.title, taskStage: linkedTask.stage, taskOverlay: linkedTask.overlay, onTaskNavigate } : {}),
            };
          })}
          {...(selectedId !== null ? { activeConversationId: selectedId } : {})}
          onNavigate={(id) => {
            handleSelect(id);
            setDrawerOpen(false);
          }}
          onNewConversation={() => { void createConversation(); }}
        />
        {selectedId && (
          <button
            aria-label="Back to conversations"
            onClick={() => handleSelect(null)}
          >
            Back
          </button>
        )}
      </div>

      {/* Desktop: inline conversation list */}
      <div className="hidden @md:block">
        <div data-testid="conversations-header" className="p-sd-3 border-b border-sd-border/50 flex items-center gap-sd-2">
          <Button variant="primary" size="sm" className="flex-1" onClick={() => { void createConversation(); }}>New Conversation</Button>
        </div>
        <ul ref={listRef} role="listbox" aria-label="Conversations" onScroll={handleListScroll}>
          {conversations.map((c) => {
            const linkedTask = taskByConversationId.get(c.id);
            return (
              <ConversationListItem
                key={c.id}
                id={c.id}
                title={c.title}
                unread={c.unread}
                agentStatus={deriveAgentStatus(linkedTask, c.processing_status)}
                {...(c.preview !== undefined ? { preview: c.preview } : {})}
                {...(c.updated_at !== undefined ? { timestamp: formatRelativeTime(c.updated_at) } : {})}
                active={c.id === selectedId}
                onClick={handleSelect}
                {...(linkedTask ? { taskLinked: true, taskId: linkedTask.id, taskTitle: linkedTask.title, taskStage: linkedTask.stage, taskOverlay: linkedTask.overlay, onTaskNavigate } : {})}
              />
            );
          })}
        </ul>
      </div>
    </PanelLayout>
    </AgentPanelsContext.Provider>
  );
}
